import { Mic, MicOff, CallEnd } from "@mui/icons-material";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { useInternalTwilioCall } from "../context/InternalTwilioCallContext";

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = String(seconds % 60).padStart(2, "0");
  return `${minutes}:${secs}`;
};

interface InternalTwilioCallViewProps {
  contactName: string;
  onEndCall: () => void;
}

export const InternalTwilioCallView = ({ contactName, onEndCall }: InternalTwilioCallViewProps) => {
  const { status, isMuted, callDuration, acceptCall, rejectCall, hangupCall, toggleMute } = useInternalTwilioCall();

  if (status === "idle") {
    onEndCall();
    return null;
  }

  if (status === "ringing-incoming") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2.5, gap: 2, alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, textAlign: "center" }}>{contactName} is calling…</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" color="error" size="small" onClick={rejectCall}>
            Decline
          </Button>
          <Button variant="contained" color="success" size="small" onClick={acceptCall}>
            Accept
          </Button>
        </Box>
      </Box>
    );
  }

  if (status === "ringing-outgoing") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2.5, gap: 2, alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, textAlign: "center" }}>Calling {contactName}…</Typography>
        <Button variant="outlined" color="error" size="small" onClick={hangupCall}>
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2.5, gap: 2, alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, textAlign: "center" }}>{contactName}</Typography>
      <Typography color="text.secondary" fontSize={13}>
        {formatDuration(callDuration)}
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        <IconButton onClick={toggleMute} sx={{ bgcolor: isMuted ? "action.selected" : "action.hover" }}>
          {isMuted ? <MicOff fontSize="small" /> : <Mic fontSize="small" />}
        </IconButton>
        <IconButton onClick={hangupCall} sx={{ bgcolor: "error.main", color: "#fff", "&:hover": { bgcolor: "error.dark" } }}>
          <CallEnd fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
